import * as Yup from 'yup';

import { formatDate } from 'utils/date';
import {
  getUploadedFile,
  makeSelectOptionsFromOptionsArray,
} from 'utils/helpers';
import {
  phoneNumber,
  validateZipCode,
  validateWebsiteUrl,
} from 'utils/validation';
import {
  EventType,
  EventTypeTitle,
  EventLocationType,
  EventLocationTypeTitle,
} from 'types';
import {
  Event,
  EventFormValues,
  eventPaymentTypeLabel,
  eventPaymentTypeOptions,
  eventPaymentTransactionLabel,
  eventPaymentTransactionOptions,
} from 'store/api/event';

const required = 'Required field';

export const validationSchema = Yup.object().shape({
  zip: validateZipCode(),
  link: validateWebsiteUrl,
  name: Yup.string().required(required),
  address: Yup.string().required(required),
  contactPhone: phoneNumber('contactPhone'),
  isManualCreditPaymentEnabled: Yup.boolean(),
  tax: Yup.number().min(0).max(100).required(),
  floorPlanLink: Yup.string().url('Must be a valid url').nullable(),
  startDate: Yup.string().required('Start date is a required field'),
  payments: Yup.array().of(
    Yup.object().shape({
      type: Yup.string().required(required),
    })
  ),
  locations: Yup.array()
    .of(
      Yup.object().shape({
        number: Yup.string().required('Number is required'),
        eventType: Yup.string().required('Event type is required'),
        locationType: Yup.string().required('Location type is required'),
      })
    )
    .min(1),
  weeklySchedule: Yup.array().of(
    Yup.object().shape({
      to: Yup.number(),
      from: Yup.number().test(
        'from-less-than-to',
        'The opening time cannot be longer than the closing time',
        function (value?: number) {
          const { to } = this.parent;

          if (!to && !value) {
            return true;
          }

          return Number(value) <= to;
        }
      ),
    })
  ),
  endDate: Yup.string()
    .required('End date is a required field')
    .test(
      'end-not-before-start',
      'End date cannot be before the start date',
      function (value?: string) {
        const start = new Date(this.parent.startDate ?? '').getTime();
        const end = new Date(value ?? '').getTime();

        if (Number.isNaN(start) || Number.isNaN(end)) {
          return true;
        }

        return end >= start;
      }
    ),
});

const weekDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const getDefaultWeeklySchedule = () =>
  weekDays.map((day) => ({
    day,
    openingTime: null,
    closingTime: null,
  }));

const normalizeZip = (zip?: string | number) => {
  if (zip === undefined || zip === null || zip === '') {
    return zip;
  }

  const zipText = String(zip);

  return zipText.length === 4 ? zipText.padStart(5, '0') : zipText;
};

export const getInitialValues = (data: Event | undefined): EventFormValues => {
  const { theme, product, ...restData } = (data ?? {}) as Event;

  const endDate = restData?.endDate ? formatDate(restData.endDate) : undefined;
  const startDate = restData?.startDate
    ? formatDate(restData.startDate)
    : undefined;

  return {
    ...restData,
    endDate,
    startDate,
    themeId: theme?.id ?? null,
    name: restData?.name ?? '',
    tax: data?.tax ?? undefined,
    productId: product?.id ?? null,
    address: restData?.address ?? '',
    zip: normalizeZip(restData?.zip),
    link: restData?.link ?? undefined,
    dates: { to: endDate, from: startDate },
    photo: getUploadedFile(restData?.photoUrl),
    weeklySchedule: data?.weeklySchedule ?? getDefaultWeeklySchedule(),
    isManualCreditPaymentEnabled: data?.isManualCreditPaymentEnabled ?? false,
    teamMembers: restData?.teamMembers
      ? restData.teamMembers.map((user) => user.id)
      : [],
    payments: restData?.payments
      ? restData.payments.map((item) => ({
          ...item,
          date: item.date ? new Date(item.date) : undefined,
        }))
      : [],
    locations: restData?.locations?.length
      ? restData.locations.map((location) => ({
          ...location,
          number: location?.number ?? undefined,
          eventType: location?.eventType ?? undefined,
          locationType: location?.locationType ?? undefined,
        }))
      : [
          {
            number: undefined,
            eventType: undefined,
            locationType: undefined,
          },
        ],
  };
};

export const paymentTypeOptions = makeSelectOptionsFromOptionsArray(
  eventPaymentTypeOptions,
  eventPaymentTypeLabel
);

export const transactionOptions = makeSelectOptionsFromOptionsArray(
  eventPaymentTransactionOptions,
  eventPaymentTransactionLabel
);

export const eventLocationTypeOptions = Object.values(EventLocationType).map(
  (item) => ({
    value: item,
    label: EventLocationTypeTitle[item],
  })
);

export const eventTypeOptions = Object.values(EventType).map((item) => ({
  value: item,
  label: EventTypeTitle[item],
}));
